import { ACCESS_KEY, BASE_URL, Data } from 'core/utilities';
import { HTTPMethod, isSuccessCode, query, Result } from '.';
import { axios } from 'core/api';

export const getApplicants = async (applicationId : string) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        return await query(
            `api/v1/mortgage/${applicationId}/applicant/`,
            { 'Authorization' : `Bearer ${token}` }
        );
    }

    return {
        success : false
    };
};

export const orderCreditCheck = async (sin : string) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
        const response = await fetch(`${BASE_URL}/api/v1/credit-check`, {
            method : HTTPMethod.POST,
            headers : {
                'Authorization' : `Bearer ${token}`,
                'Content-Type' : 'application/json'
            },
            body : JSON.stringify({ sin })
        });

        const { message } = await response.json();

        return {
            success : isSuccessCode(response.status),
            error : message
        };
    } else {
        return {
            success : false,
            error : 'Cannot complete credit check, you are currently logged out. Please log back in.'
        };
    }
};

export const getDocumentTypes = async () : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        let page = 1;
        let types : Array<Data> = [];

        /* eslint-disable no-constant-condition */
        while (true) {
            const {
                success, 
                reply 
            } = await query(
                `api/v1/mortgage/documents/type/?page=${page}`,
                { 'Authorization' : `Bearer ${token}` }
            );

            page += 1;

            if (success) {
                const { next, results } = reply as { next : string | null; results : Array<Data> };

                if (results) {
                    types = types.concat(results);
                } else break;
                if (next === null) break;
            } else {
                return {
                    success
                };
            }
        }

        return {
            success : true,
            reply : {
                types
            }
        };
    }

    return {
        success : false
    };
};

export const getUserUploadedDocuments = async (applicationId : string) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        let page = 1;
        let uploadedDocuments : Array<Data> = [];

        /* eslint-disable no-constant-condition */
        while (true) {
            const {
                success, 
                reply 
            } = await query(
                `api/v1/mortgage/${applicationId}/documents/?page=${page}`,
                { 'Authorization' : `Bearer ${token}` }
            );

            page += 1;

            if (success) {
                const { next, results } = reply as { next : string | null; results : Array<Data> };

                if (results) {
                    uploadedDocuments = uploadedDocuments.concat(results);
                } else break;
                if (next === null) break;
            } else {
                return {
                    success
                };
            }
        }

        return {
            success : true,
            reply : {
                uploadedDocuments : uploadedDocuments.filter(({ internal } : Data) => !internal)
            }
        };
    }

    return {
        success : false
    };
};

export const uploadDocument = async (applicationId : string, body : FormData) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/documents/`, {
            method : HTTPMethod.POST,
            headers : {
                'Authorization' : `Bearer ${token}`
            },
            body
        });

        const data = await response.json();
        
        return {
            success: isSuccessCode(response.status),
            data,
        };
    } else {
        return {
            success: false
        };
    }
};

export const deleteDocument = async (applicationId : string, documentId : string) : Promise<boolean> => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/documents/${documentId}/`, {
            method : HTTPMethod.DELETE,
            headers : {
                'Authorization' : `Bearer ${token}`
            }
        });

        return isSuccessCode(response.status);
    } else {
        return false;
    }
};

export const markReadyForReview = async (applicationId : string, applicantId : string) : Promise<boolean> => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/${applicantId}/review/`, {
            method : HTTPMethod.PATCH,
            headers : {
                'Authorization' : `Bearer ${token}`
            }
        });

        return isSuccessCode(response.status);
    } else {
        return false;
    }
};

export const updateApplicationStatusToUnderwriting = async (applicationId: string) => {
    const response = await axios.patch(`/api/v1/mortgage/${applicationId}/underwriting/`);

    const { status } = response;

    if (status === 200) {
        return isSuccessCode(status);
    } else {
        return false;
    }
};
